// Extra CMS slots for the 'home' page — added to make previously-hardcoded
// public marketing copy editable. Concatenated into CMS_SLOTS via ./index.
// Each default MUST equal the exact copy the component rendered before wiring,
// so the public page is byte-identical until an admin edits it.
import type { SlotDef } from '../slots';

export const homeExtra: SlotDef[] = [
  // ─── Step 1 — Hero (StepInit) ─────────────────────────────────────────
  {
    key: 'home.step_init.founding_strip_label',
    page: 'home',
    kind: 'markdown',
    label: 'Step 1: Founding-host strip label',
    helpText: 'Small label above the named founding venues (e.g. "Said yes first").',
    default: 'Said yes first',
  },
  {
    key: 'home.step_init.founding_strip',
    page: 'home',
    kind: 'bullet_list',
    label: 'Step 1: Founding-host venues',
    helpText:
      'One founding-host venue per line (name + city). They render on one line, joined by dots. The "+N more, see all" link is added automatically once more founding venues have claimed a spot than are named here.',
    default: [
      'The Lantern Room (Brisbane)',
      'Kookaburra Field (Maleny)',
      'High Tide Cafe (Burleigh Heads)',
      'Garden House Sessions (Toowoomba)',
      'The Back Room (Noosaville)',
    ],
  },
  {
    key: 'home.step_init.scroll_hint',
    page: 'home',
    kind: 'markdown',
    label: 'Step 1: Scroll hint',
    helpText:
      'The clickable "next section" hint at the bottom of the hero. It leads into the "What is wwaave?" definition directly below. A down-arrow is added automatically.',
    default: 'What is wwaave?',
  },

  // ─── What is wwaave? — the definition card (StepWhatIs) ───────────────
  // Sits directly below the hero. Richie asked for it as a dictionary entry
  // (2026-08-20): the headword and its pronunciation on one line, then the
  // definition underneath. The whole point of the section is that the wording
  // is his, so every line of the card is a slot and any of them can be blanked
  // to disappear.
  {
    key: 'home.what_is.eyebrow',
    page: 'home',
    kind: 'markdown',
    label: 'What is wwaave: Eyebrow',
    helpText: 'Small gold label above the heading. Leave blank to hide it.',
    default: 'The short version',
  },
  {
    key: 'home.what_is.headline',
    page: 'home',
    kind: 'markdown',
    label: 'What is wwaave: Heading',
    helpText: 'The section heading above the definition card.',
    default: 'What is wwaave?',
  },
  {
    key: 'home.what_is.headword',
    page: 'home',
    kind: 'markdown',
    label: 'What is wwaave: Headword',
    helpText: 'The big word at the top of the card, set like a dictionary entry.',
    default: 'wwaave',
  },
  {
    key: 'home.what_is.pronunciation',
    page: 'home',
    kind: 'markdown',
    label: 'What is wwaave: Pronunciation',
    helpText:
      'The italic note beside the headword, e.g. "/wāv/ n." Leave blank to show the headword on its own.',
    default: '/wāv/ n.',
  },
  {
    key: 'home.what_is.body',
    page: 'home',
    kind: 'markdown',
    label: 'What is wwaave: Definition',
    helpText:
      'The definition itself. Press Enter once for a new line, and leave a blank line between sentences to put a gap between them on the page.',
    default:
      '[worldwide artist and venue exchange]\n' +
      'wwaave is a web platform and global network that connects artists and creatives with host venues. ' +
      'It promotes mutual exchange through a non-monetary system of creative contribution for hospitality.\n' +
      'syn. like wwoofing for artists or couchsurfing for creatives. A connected community.',
  },
  {
    key: 'home.what_is.image',
    page: 'home',
    kind: 'image',
    label: 'What is wwaave: Artwork',
    helpText:
      'Optional artwork across the top of the card. Blank by default, which renders the card as type alone — upload one to add it.',
    default: { url: '', alt: '' },
  },
  {
    key: 'home.what_is.scroll_hint',
    page: 'home',
    kind: 'markdown',
    label: 'What is wwaave: Scroll hint',
    helpText:
      'The clickable "next section" hint at the bottom of this section. It leads into the "Who\'s on wwaave" band directly below. A down-arrow is added automatically.',
    default: "See who's on wwaave",
  },

  // ─── Shared artist / host CTA button labels (hero + closing split) ─────
  {
    key: 'home.cta.artist',
    page: 'home',
    kind: 'markdown',
    label: 'Artist CTA button',
    helpText: 'Label on the "artist" sign-up button. Shared by the hero (Step 1) and the closing split (Step 6).',
    default: "I'm an artist",
  },
  {
    key: 'home.cta.host',
    page: 'home',
    kind: 'markdown',
    label: 'Host CTA button',
    helpText: 'Label on the "host" sign-up button. Shared by the hero (Step 1) and the closing split (Step 6).',
    default: 'I host a venue',
  },

  // ─── Step 2 — How it works (StepHowItWorks) ───────────────────────────
  {
    key: 'home.step_how.scroll_hint',
    page: 'home',
    kind: 'markdown',
    label: 'Step 2: Scroll hint',
    helpText:
      'The clickable "next section" hint at the bottom of the How-it-works step. A down-arrow is added automatically.',
    // Was "See the Smart Rider" until the Smart Rider moved to the How It Works
    // page and the "Inside wwaave" preview took its place in the scroll chain.
    default: 'See inside wwaave',
  },

  // ─── Inside wwaave — the product preview (StepInside) ─────────────────
  // Four product views the reader can switch between, each with its own pair of
  // screenshots (a desktop shot + a phone shot) and three brand-voice callouts.
  // The view order, the icons and which callout sits where are component-owned
  // and fixed by position — only the wording and the images travel in a slot.
  //
  // ON-PAGE ORDER (2026-08-13, Richie, feedback 9akIHs5yr): Browse, Profile,
  // Dashboard, Inbox. The four blocks below are kept in that same order, and
  // each label names its view, so what an admin edits top-to-bottom is what a
  // visitor meets left-to-right. `getSlotDefsForPage` preserves array order,
  // which is the only thing block position controls — every lookup is by key,
  // so moving a block can never re-point an override row.
  {
    key: 'home.step_inside.eyebrow',
    page: 'home',
    kind: 'markdown',
    label: 'Inside wwaave: Eyebrow',
    default: 'Inside wwaave',
    // NOT retired. "Take out the eyebrow completely" (Richie, feedback
    // HpoZ1F0of6m5XKnkdZ99t) is one of four sentences in his Browse bullet, and
    // the other three are unambiguously about the screenshot: the sidebar and
    // filters, the word "rooms", the "128 rooms" counter. The eyebrow he means
    // is the mock Browse screen's own, which read "Find your next room" and has
    // gone along with the counter. Retiring this slot would take a section
    // header he never named off the page, and leave him no way to put it back
    // from /admin.
  },
  {
    key: 'home.step_inside.headline',
    page: 'home',
    kind: 'markdown',
    label: 'Inside wwaave: Headline',
    helpText:
      'Two lines. Put a line break where you want the headline to wrap (each line renders on its own row).',
    default: 'This is what it looks like\nfrom the inside.',
  },
  {
    key: 'home.step_inside.sub',
    page: 'home',
    kind: 'markdown',
    label: 'Inside wwaave: Sub-line',
    helpText:
      'The four things named here are the four tabs, in the order they appear: Browse, Profile, Dashboard, Inbox. If the tab order ever changes, this sentence has to change with it.',
    default:
      'Four screens you actually live in: find a venue, show who you are, see your week, keep the thread. No sign-up needed to look.',
  },
  {
    key: 'home.step_inside.scroll_hint',
    page: 'home',
    kind: 'markdown',
    label: 'Inside wwaave: Scroll hint',
    helpText:
      'The clickable "next section" hint at the bottom of the preview, leading into the founding offer. A down-arrow is added automatically.',
    default: 'Become a founding venue',
  },
  {
    key: 'home.step_inside.swipe_hint',
    page: 'home',
    kind: 'markdown',
    label: 'Inside wwaave: Mobile swipe hint',
    helpText:
      'Small line shown under the preview on phones only, where the views are a swipeable carousel rather than tabs.',
    default: 'Swipe to see more',
  },

  // ── Browse (tab 1 of 4) ──
  {
    key: 'home.step_inside.browse_tab',
    page: 'home',
    kind: 'markdown',
    label: 'Inside wwaave: Browse tab label',
    helpText: 'Keep it to one or two words — these sit side by side in a switcher.',
    default: 'Browse',
  },
  {
    key: 'home.step_inside.browse_shot',
    page: 'home',
    kind: 'image',
    label: 'Inside wwaave: Browse desktop screenshot',
    helpText:
      'The desktop screenshot for this view. Leave alt blank — the screen-reader description below is what gets announced.',
    default: { url: '/img/inside/browse-desktop.avif', alt: '' },
  },
  {
    key: 'home.step_inside.browse_shot_mobile',
    page: 'home',
    kind: 'image',
    label: 'Inside wwaave: Browse phone screenshot',
    helpText:
      'The phone screenshot, shown to phone visitors in place of the desktop one. Leave blank to show the desktop shot at every width.',
    default: { url: '/img/inside/browse-mobile.avif', alt: '' },
  },
  {
    key: 'home.step_inside.browse_aria',
    page: 'home',
    kind: 'markdown',
    label: 'Inside wwaave: Browse screen-reader description',
    helpText:
      'What a screen reader announces for this screenshot. Describe what the screen shows, not the fact that it is an image.',
    default:
      'The wwaave browse screen, showing host venues as cards alongside search, filters and a toggle for map view',
  },
  {
    key: 'home.step_inside.browse_callouts',
    page: 'home',
    kind: 'step_list',
    label: 'Inside wwaave: Browse callouts',
    helpText:
      'Three callout boxes beside the screenshot. Title is the punchy line, body is the plain-language explanation. Keep it to three — the fourth would not fit the layout. The icons are fixed by position.',
    default: [
      {
        title: 'Search the way you tour.',
        body: 'Filter by city, art form, capacity and what a host can offer. Map view shows what is along the route.',
      },
      {
        title: 'Every venue is somewhere real.',
        body: 'Photos of the space, the art forms they welcome, and what they offer in return. No stock photos, no mystery.',
      },
      {
        title: 'Free to look, free to ask.',
        body: 'Artists never pay to browse or to send a request. Host venues carry the subscription.',
      },
    ],
  },

  // ── Profile (tab 2 of 4) ──
  {
    key: 'home.step_inside.profile_tab',
    page: 'home',
    kind: 'markdown',
    label: 'Inside wwaave: Profile tab label',
    default: 'Profile',
  },
  {
    key: 'home.step_inside.profile_shot',
    page: 'home',
    kind: 'image',
    label: 'Inside wwaave: Profile desktop screenshot',
    helpText:
      'The desktop screenshot for this view. Leave alt blank — the screen-reader description below is what gets announced.',
    default: { url: '/img/inside/profile-desktop.avif', alt: '' },
  },
  {
    key: 'home.step_inside.profile_shot_mobile',
    page: 'home',
    kind: 'image',
    label: 'Inside wwaave: Profile phone screenshot',
    helpText:
      'The phone screenshot, shown to phone visitors in place of the desktop one. Leave blank to show the desktop shot at every width.',
    default: { url: '/img/inside/profile-mobile.avif', alt: '' },
  },
  {
    key: 'home.step_inside.profile_aria',
    page: 'home',
    kind: 'markdown',
    label: 'Inside wwaave: Profile screen-reader description',
    default:
      'A wwaave artist profile, showing photos, art forms, links and completed exchanges',
  },
  {
    key: 'home.step_inside.profile_callouts',
    page: 'home',
    kind: 'step_list',
    label: 'Inside wwaave: Profile callouts',
    helpText:
      'Three callout boxes beside the screenshot. Title is the punchy line, body is the plain-language explanation. The icons are fixed by position.',
    default: [
      {
        title: 'The one link you send.',
        body: 'Your art forms, your photos, your links. Send it once and let it do the talking.',
      },
      {
        title: 'Verified means verified.',
        body: 'Identity is checked before a stay is confirmed, and it runs in both directions.',
      },
      {
        title: 'Your history travels with you.',
        body: 'Every completed exchange leaves a permanent mark on your profile that the next host can read.',
      },
    ],
  },

  // ── Dashboard (tab 3 of 4) ──
  {
    key: 'home.step_inside.dashboard_tab',
    page: 'home',
    kind: 'markdown',
    label: 'Inside wwaave: Dashboard tab label',
    default: 'Dashboard',
  },
  {
    key: 'home.step_inside.dashboard_shot',
    page: 'home',
    kind: 'image',
    label: 'Inside wwaave: Dashboard desktop screenshot',
    helpText:
      'The desktop screenshot for this view. Leave alt blank — the screen-reader description below is what gets announced.',
    default: { url: '/img/inside/dashboard-desktop.avif', alt: '' },
  },
  {
    key: 'home.step_inside.dashboard_shot_mobile',
    page: 'home',
    kind: 'image',
    label: 'Inside wwaave: Dashboard phone screenshot',
    helpText:
      'The phone screenshot, shown to phone visitors in place of the desktop one. Leave blank to show the desktop shot at every width.',
    default: { url: '/img/inside/dashboard-mobile.avif', alt: '' },
  },
  {
    key: 'home.step_inside.dashboard_aria',
    page: 'home',
    kind: 'markdown',
    label: 'Inside wwaave: Dashboard screen-reader description',
    default:
      'The wwaave dashboard, showing confirmed exchanges, what needs an answer, and recent activity',
  },
  {
    key: 'home.step_inside.dashboard_callouts',
    page: 'home',
    kind: 'step_list',
    label: 'Inside wwaave: Dashboard callouts',
    helpText:
      'Three callout boxes beside the screenshot. Title is the punchy line, body is the plain-language explanation. The icons are fixed by position.',
    default: [
      {
        title: 'Your week, on one screen.',
        body: 'What is confirmed, what is waiting on you, and what needs an answer today.',
      },
      {
        title: 'Nothing slips past its date.',
        body: 'Requests, replies and travel dates all surface here, so the thing you needed to answer does not sit unread.',
      },
      {
        title: 'Typed once, used everywhere.',
        body: 'Dates, gear and hospitality carry through from your profile into every request you send.',
      },
    ],
  },

  // ── Inbox (tab 4 of 4) ──
  {
    key: 'home.step_inside.inbox_tab',
    page: 'home',
    kind: 'markdown',
    label: 'Inside wwaave: Inbox tab label',
    default: 'Inbox',
  },
  {
    key: 'home.step_inside.inbox_shot',
    page: 'home',
    kind: 'image',
    label: 'Inside wwaave: Inbox desktop screenshot',
    helpText:
      'The desktop screenshot for this view. Leave alt blank — the screen-reader description below is what gets announced.',
    default: { url: '/img/inside/inbox-desktop.avif', alt: '' },
  },
  {
    key: 'home.step_inside.inbox_shot_mobile',
    page: 'home',
    kind: 'image',
    label: 'Inside wwaave: Inbox phone screenshot',
    helpText:
      'The phone screenshot, shown to phone visitors in place of the desktop one. Leave blank to show the desktop shot at every width.',
    default: { url: '/img/inside/inbox-mobile.avif', alt: '' },
  },
  {
    key: 'home.step_inside.inbox_aria',
    page: 'home',
    kind: 'markdown',
    label: 'Inside wwaave: Inbox screen-reader description',
    default: 'The wwaave inbox, showing a list of conversations beside an open thread',
  },
  {
    key: 'home.step_inside.inbox_callouts',
    page: 'home',
    kind: 'step_list',
    label: 'Inside wwaave: Inbox callouts',
    helpText:
      'Three callout boxes beside the screenshot. Title is the punchy line, body is the plain-language explanation. The icons are fixed by position.',
    default: [
      {
        title: 'A letter, not a form.',
        body: 'The first message is a real note between two people. Dates, what you would bring, why this venue.',
      },
      {
        title: 'One thread holds the lot.',
        body: 'The rider, arrival details and the address once it is confirmed. No lost email chains, no group chat archaeology.',
      },
      {
        title: 'The record is yours.',
        body: 'What you agreed stays in writing, and it is still there months later if either of you needs it.',
      },
    ],
  },

  // ─── Who's on wwaave (StepWhosOn) — dual video marquee interlude ───────
  {
    key: 'home.step_network.eyebrow',
    page: 'home',
    kind: 'markdown',
    label: "Who's on wwaave: Eyebrow",
    default: "Who's on wwaave",
  },
  {
    key: 'home.step_network.headline',
    page: 'home',
    kind: 'markdown',
    label: "Who's on wwaave: Headline",
    helpText:
      'Two lines. Put a line break where you want the headline to wrap (each line renders on its own row).',
    default: 'a DJ for your distillery.\na poet for your bookshop.',
  },
  {
    key: 'home.step_network.sub',
    page: 'home',
    kind: 'markdown',
    label: "Who's on wwaave: Sub-line",
    default:
      'wwaave matches independent artists with venues that want more than background noise. Real people, real rooms. You find each other here, then shape the exchange together.',
  },
  {
    key: 'home.step_network.scroll_hint',
    page: 'home',
    kind: 'markdown',
    label: "Who's on wwaave: Scroll hint",
    helpText:
      'The clickable "next section" hint at the bottom of the "Who\'s on wwaave" band, leading into How it works. A down-arrow is added automatically.',
    default: 'See how it works',
  },
  {
    key: 'home.step_network.row_aria_creatives',
    page: 'home',
    kind: 'markdown',
    label: "Who's on wwaave: Row screen-reader label",
    helpText:
      'Accessible label for the marquee row. The tiles are decorative, so this label is what a screen reader announces for the whole strip.',
    default: 'Artists and venues currently on wwaave',
  },
  {
    key: 'home.step_network.row_aria_venues',
    page: 'home',
    kind: 'markdown',
    label: "Who's on wwaave: Bottom row screen-reader label",
    helpText:
      'Accessible label for the SECOND (lower) marquee row. The tiles are decorative, so this is what a screen reader announces for the whole lower strip.',
    default: 'More artists and venues currently on wwaave',
  },
  // The two scrolling rows of the "Who's on wwaave" strip. Each row is an
  // ordered gallery of up to 40 tiles the team manages from Admin → CMS → Home
  // (add / remove / reorder / swap each tile). The top row scrolls left, the
  // bottom row scrolls right. Tiles are decorative 16:9 images — the row's
  // screen-reader label (above) describes the whole strip, so leave alt blank.
  // Each row's default holds the original six concept stills so the band looks
  // full before the team curates real artists-in-venues. (Superseded the old
  // fixed tile_1..6 single-image slots, retired just below.)
  {
    key: 'home.step_network.row_top',
    page: 'home',
    kind: 'image_list',
    label: "Who's on wwaave: Top row images",
    helpText:
      'The upper marquee row (scrolls left). Add up to 40 tiles; drag to reorder. Each is a 16:9 image of an artist in a venue. Leave alt blank — the strip is decorative.',
    default: [
      { url: '/video/posters/collision-hoop.jpg', alt: '' },
      { url: '/video/posters/collision-poet.jpg', alt: '' },
      { url: '/video/posters/collision-dj.jpg', alt: '' },
      { url: '/video/posters/collision-comedian.jpg', alt: '' },
      { url: '/video/posters/collision-muralist.jpg', alt: '' },
      { url: '/video/posters/collision-dj2.jpg', alt: '' },
    ],
  },
  {
    key: 'home.step_network.row_bottom',
    page: 'home',
    kind: 'image_list',
    label: "Who's on wwaave: Bottom row images",
    helpText:
      'The lower marquee row (scrolls right). Add up to 40 tiles; drag to reorder. Each is a 16:9 image of an artist in a venue. Leave alt blank — the strip is decorative.',
    default: [
      { url: '/video/posters/collision-dj2.jpg', alt: '' },
      { url: '/video/posters/collision-muralist.jpg', alt: '' },
      { url: '/video/posters/collision-comedian.jpg', alt: '' },
      { url: '/video/posters/collision-dj.jpg', alt: '' },
      { url: '/video/posters/collision-poet.jpg', alt: '' },
      { url: '/video/posters/collision-hoop.jpg', alt: '' },
    ],
  },
  // Retired: the marquee's six fixed single-image slots were replaced by the two
  // image_list rows above (unlimited, reorderable). Kept (deprecated, never
  // deleted) so any stray override row can't surface as a dead field.
  {
    key: 'home.step_network.tile_1',
    page: 'home',
    kind: 'image',
    deprecated: true,
    label: "Who's on wwaave: Tile 1 (retired)",
    helpText: 'Retired — the marquee is now two editable galleries (Top/Bottom row images).',
    default: { url: '/video/posters/collision-hoop.jpg', alt: '' },
  },
  {
    key: 'home.step_network.tile_2',
    page: 'home',
    kind: 'image',
    deprecated: true,
    label: "Who's on wwaave: Tile 2 (retired)",
    helpText: 'Retired — the marquee is now two editable galleries (Top/Bottom row images).',
    default: { url: '/video/posters/collision-poet.jpg', alt: '' },
  },
  {
    key: 'home.step_network.tile_3',
    page: 'home',
    kind: 'image',
    deprecated: true,
    label: "Who's on wwaave: Tile 3 (retired)",
    helpText: 'Retired — the marquee is now two editable galleries (Top/Bottom row images).',
    default: { url: '/video/posters/collision-dj.jpg', alt: '' },
  },
  {
    key: 'home.step_network.tile_4',
    page: 'home',
    kind: 'image',
    deprecated: true,
    label: "Who's on wwaave: Tile 4 (retired)",
    helpText: 'Retired — the marquee is now two editable galleries (Top/Bottom row images).',
    default: { url: '/video/posters/collision-comedian.jpg', alt: '' },
  },
  {
    key: 'home.step_network.tile_5',
    page: 'home',
    kind: 'image',
    deprecated: true,
    label: "Who's on wwaave: Tile 5 (retired)",
    helpText: 'Retired — the marquee is now two editable galleries (Top/Bottom row images).',
    default: { url: '/video/posters/collision-muralist.jpg', alt: '' },
  },
  {
    key: 'home.step_network.tile_6',
    page: 'home',
    kind: 'image',
    deprecated: true,
    label: "Who's on wwaave: Tile 6 (retired)",
    helpText: 'Retired — the marquee is now two editable galleries (Top/Bottom row images).',
    default: { url: '/video/posters/collision-dj2.jpg', alt: '' },
  },

  // ─── Step 3 — The Smart Rider (retired from the homepage) ─────────────
  // The Smart Rider moved to the How It Works page, where the surrounding steps
  // give it the context it lacked on the homepage (Richie, 2026-08-09). Its copy
  // now lives under how_it_works.rider_*; this slot is kept (append-only) so the
  // existing override row doesn't orphan.
  {
    key: 'home.step_rider.scroll_hint',
    page: 'home',
    kind: 'markdown',
    deprecated: true,
    label: 'Step 3: Scroll hint (retired)',
    helpText:
      'Retired — the Smart Rider moved to the How It Works page. Edit it under How It Works.',
    default: 'The WWAAVE Promise',
  },

  // ─── Step 4 — The WWAAVE Promise (StepLogic) ──────────────────────────
  {
    key: 'home.step_logic.scroll_hint',
    page: 'home',
    kind: 'markdown',
    label: 'Step 4: Scroll hint',
    helpText: 'The clickable "next section" hint at the bottom of the Promise step. A down-arrow is added automatically.',
    default: 'Become a founding venue',
  },

  // ─── Step 5 — Founding offer (StepFoundingOffer) ──────────────────────
  // (N33) The waitlist_* slots below are retired — the section is now a
  // single centered founding-offer card, no right-hand waitlist capture.
  {
    key: 'home.step_founding.cta_available',
    page: 'home',
    kind: 'markdown',
    label: 'Step 5: Offer CTA (spots left)',
    helpText: 'Founding-offer button label while founding spots remain. Links to sign-up (fixed).',
    default: 'Claim a founding spot',
  },
  {
    key: 'home.step_founding.cta_full',
    page: 'home',
    kind: 'markdown',
    label: 'Step 5: Offer CTA (cohort full)',
    helpText: 'Founding-offer button label once every founding spot is taken. Links to sign-up (fixed).',
    default: 'Become a founding venue',
  },
  {
    key: 'home.step_founding.offer_link',
    page: 'home',
    kind: 'markdown',
    label: 'Step 5: "Read the full offer" link',
    helpText: 'Text of the link under the offer button. The arrow and destination (the founding-venue offer page) are fixed.',
    default: 'Read the full founding offer',
  },
  {
    // Retired (N33): the founding-offer section is now a single card and no
    // longer renders the right-hand waitlist capture. Kept (deprecated, never
    // deleted) so any stray override row can't surface as a dead field.
    key: 'home.step_founding.waitlist_eyebrow',
    page: 'home',
    kind: 'markdown',
    deprecated: true,
    label: 'Step 5: Waitlist eyebrow (retired)',
    helpText: 'Retired — the waitlist card was removed from the founding-offer section.',
    default: 'Not ready yet?',
  },
  {
    key: 'home.step_founding.waitlist_submit',
    page: 'home',
    kind: 'markdown',
    deprecated: true,
    label: 'Step 5: Waitlist submit button (retired)',
    helpText: 'Retired — the waitlist card was removed from the founding-offer section.',
    default: 'Keep me posted',
  },
  {
    key: 'home.step_founding.waitlist_success_eyebrow',
    page: 'home',
    kind: 'markdown',
    deprecated: true,
    label: 'Step 5: Waitlist thank-you eyebrow (retired)',
    helpText: 'Retired — the waitlist card was removed from the founding-offer section.',
    default: "You're on the list",
  },
  {
    key: 'home.step_founding.waitlist_success_headline',
    page: 'home',
    kind: 'markdown',
    deprecated: true,
    label: 'Step 5: Waitlist thank-you headline (retired)',
    helpText: 'Retired — the waitlist card was removed from the founding-offer section.',
    default: 'Thanks for the interest.',
  },
  {
    key: 'home.step_founding.waitlist_success_body',
    page: 'home',
    kind: 'markdown',
    deprecated: true,
    label: 'Step 5: Waitlist thank-you body (retired)',
    helpText: 'Retired — the waitlist card was removed from the founding-offer section.',
    default:
      "We'll send a note when there's something worth your time. No spam, no packaging you up for anyone else.",
  },
  {
    key: 'home.step_founding.scroll_hint',
    page: 'home',
    kind: 'markdown',
    label: 'Step 5: Scroll hint',
    helpText: 'The clickable "next section" hint at the bottom of the founding-offer step. A down-arrow is added automatically.',
    default: 'Your turn',
  },

  // ─── Sticky join pill (StickyJoinCta) ─────────────────────────────────
  {
    key: 'home.sticky_cta.button',
    page: 'home',
    kind: 'markdown',
    label: 'Sticky sign-up pill button',
    helpText: 'Label on the persistent sign-up button. The pill text sits in "Sticky sign-up pill text".',
    default: 'Sign up',
  },

  // ─── Postscript link cards (MarketingPostscript) ──────────────────────
  // Each card's destination is fixed in code; only the title + description are
  // editable, one slot each (static per-card — never zipped by index). These
  // replace the retired positional list slot home.postscript.tiles.
  {
    key: 'home.postscript.tile_how_title',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 1 (How it works): title',
    default: 'How it works',
  },
  {
    key: 'home.postscript.tile_how_body',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 1 (How it works): description',
    default: 'Browse, write a request, stay, leave a review. The flow in detail.',
  },
  // Card 2 links to /what-we-are-and-arent, which is now called "Our Ethos"
  // (Richie, 2026-08-18 — feedback y5uYAuKIbZM4aNSh323Lx). The URL and the
  // PageId are unchanged; only the name a reader is sent to moves.
  {
    key: 'home.postscript.tile_promise_title',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 2 (Our Ethos): title',
    default: 'Our Ethos',
  },
  {
    key: 'home.postscript.tile_promise_body',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 2 (Our Ethos): description',
    // The trailing sentence used to be the page's old TITLE verbatim ("What
    // WWAAVE is and isn't"), which read oddly under a card now headed Our Ethos
    // and named a contrast section removed on 2026-08-18.
    default:
      'No cuts, no premium-placement paywalls, no data sales. The principles behind every decision.',
  },
  {
    key: 'home.postscript.tile_guidelines_title',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 3 (How we hold the line): title',
    default: 'How we hold the line',
  },
  {
    key: 'home.postscript.tile_guidelines_body',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 3 (How we hold the line): description',
    default: 'Community standards and how we keep the network honest.',
  },
  {
    key: 'home.postscript.tile_founding_title',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 4 (Founding venues): title',
    default: 'Founding venues',
  },
  {
    key: 'home.postscript.tile_founding_body',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 4 (Founding venues): description',
    default:
      'Spots are filling. A free first year, then the founding rate ($19 AUD/month, locked for life), closes once 50 venues join.',
  },
  {
    key: 'home.postscript.tile_about_title',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 5 (About): title',
    default: 'Run by a muso, not a tech bro',
  },
  {
    key: 'home.postscript.tile_about_body',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 5 (About): description',
    default:
      "Richie's story, from a worm farm in Bulgaria to the platform he wished existed. From a small ripple, wwaaves are made.",
  },
  {
    key: 'home.postscript.tile_notes_title',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 6 (Notes from the road): title',
    default: 'Notes from the road',
  },
  {
    key: 'home.postscript.tile_notes_body',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 6 (Notes from the road): description',
    default: 'Quarterly letters from Richie about what’s working and what isn’t.',
  },
  {
    key: 'home.postscript.tile_faq_title',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 7 (FAQ): title',
    default: 'FAQ',
  },
  {
    key: 'home.postscript.tile_faq_body',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript card 7 (FAQ): description',
    default: 'Five questions we hear most: free?, verification, disputes, reviews, where we’re live.',
  },
];
